import BaseToast from './BaseToast';
import removeNotification from '../actions/removeNotification';
import updateNotificiationIgnore from '../actions/updateNotificationIgnore';
import updateNotificationTimedOut from '../actions/updateNotificationTimedOut';
import SkypeNotificationViewState from '../store/schema/SkypeNotificationViewState';
import { handleNotification } from '../utils/notificationHandler';
import { observer } from 'mobx-react';
import { ActionButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { getPackageBaseUrl } from 'owa-config';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import * as React from 'react';
import {
    CALL_NOTIFICATION_TYPE,
    IM_NOTIFICATION_TYPE,
    SUMMARY_NOTIFICATION_TYPE,
} from '../utils/constants';

let styles = require('./SkypeToast.scss');
let classNames = require('classnames/bind').bind(styles);

const IM_NOTIFICATION_TIMEOUT = 20000;
const CALL_NOTIFICATION_TIMEOUT = 60000;

const IM_AUDIO_FILENAME = 'SkypeNotification.mp3';
const CALL_AUDIO_FILENAME = 'SkypeRinging.mp3';

@observer
export default class SkypeToast extends BaseToast {
    private timer: NodeJS.Timer;

    render() {
        let { playSound, type } = this.props.notification as SkypeNotificationViewState;

        return (
            <div className={styles.container}>
                <ActionButton
                    styles={{
                        flexContainer: styles.notificationButtonFlexContainer,
                    }}
                    onClick={this.onActionClick}>
                    {this.onRenderNotificationType(type)}
                    {playSound && this.renderAudio()}
                </ActionButton>
                <IconButton
                    className={styles.dismissButton}
                    iconProps={{
                        iconName: ControlIcons.Cancel,
                        styles: {
                            root: styles.dismissButtonIcon,
                        },
                    }}
                    onClick={this.onCancelClick}
                />
            </div>
        );
    }

    private getIconName = () => {
        const isBusiness = isFeatureEnabled('fwk-skypeBusinessV2');
        return isBusiness ? ControlIcons.OfficeChat : ControlIcons.SkypeLogo;
    };

    componentDidMount() {
        let { type } = this.props.notification as SkypeNotificationViewState;

        let displayTime = this.getNotificationDisplayTime(type);

        if (displayTime) {
            this.timer = setTimeout(() => {
                updateNotificationTimedOut(this.props.notification, true);
                handleNotification(this.props.notification);
                removeNotification(this.props.notification);
                this.timer = null;
            }, displayTime);
        }
    }

    componentWillUnmount() {
        this.clearTimer();
    }

    private onRenderNotificationType(notificationtype: string) {
        switch (notificationtype) {
            case SUMMARY_NOTIFICATION_TYPE:
                return this.onRenderSummaryNotification();
            case IM_NOTIFICATION_TYPE:
                return this.onRenderMessageNotification();
            case CALL_NOTIFICATION_TYPE:
                return this.onRenderCallingNotification();
            default:
                return null;
        }
    }

    private onRenderSummaryNotification() {
        let { description, type } = this.props.notification as SkypeNotificationViewState;

        return (
            <div className={styles.dataContainer}>
                {this.renderImage(type)}
                <div className={classNames(styles.data)}>
                    <div
                        className={classNames(
                            styles.title,
                            styles.neutralPrimaryText,
                            styles.summaryText
                        )}>
                        {description}
                    </div>
                </div>
            </div>
        );
    }

    private onRenderMessageNotification() {
        let { title, description, type } = this.props.notification as SkypeNotificationViewState;

        return (
            <div className={styles.dataContainer}>
                {this.renderImage(type)}
                <div className={classNames(styles.data)}>
                    <div className={classNames(styles.title, styles.neutralPrimaryText)}>
                        {title}
                    </div>
                    <div
                        className={classNames(
                            styles.descriptionContainer,
                            styles.neutralPrimaryText
                        )}>
                        <Icon
                            iconName={this.getIconName()}
                            className={styles.skypeIconDescription}
                        />
                        <div className={styles.description}>{description}</div>
                    </div>
                </div>
            </div>
        );
    }

    private onRenderCallingNotification() {
        let { title, description, type } = this.props.notification as SkypeNotificationViewState;

        return (
            <div className={styles.dataContainer}>
                {this.renderImage(type)}
                <div className={styles.callingData}>
                    <div className={classNames(styles.title, styles.neutralPrimaryText)}>
                        {title}
                    </div>
                    <div
                        className={classNames(
                            styles.descriptionContainer,
                            styles.themePrimaryText
                        )}>
                        <Icon
                            iconName={this.getIconName()}
                            className={styles.skypeIconDescription}
                        />
                        <div className={styles.title}>{description}</div>
                    </div>
                </div>
                <div className={styles.skypeActionsContainer}>
                    <IconButton
                        className={classNames(styles.declineAction, styles.skypeActions)}
                        iconProps={{
                            iconName: ControlIcons.DeclineCall,
                        }}
                        onClick={this.onCancelClick}
                    />
                    <IconButton
                        className={classNames(styles.acceptAction, styles.skypeActions)}
                        iconProps={{
                            iconName: ControlIcons.Phone,
                        }}
                        onClick={this.onActionClick}
                    />
                </div>
            </div>
        );
    }

    protected onCancelClick = (e: React.MouseEvent<unknown>) => {
        e.stopPropagation();
        updateNotificiationIgnore(this.props.notification, true);
        handleNotification(this.props.notification);

        this.onNotificationRemoved();
    };

    private renderImage(notificationType: string): JSX.Element {
        if (notificationType == SUMMARY_NOTIFICATION_TYPE) {
            return (
                <Icon
                    iconName={this.getIconName()}
                    className={classNames(styles.headerSkypeIcon, styles.headerImage)}
                />
            );
        } else {
            const { image, smtpAddress, displayName } = this.props
                .notification as SkypeNotificationViewState;
            if (image) {
                return (
                    <span>
                        <img
                            className={classNames(styles.headerImage, styles.senderImage)}
                            src={image}
                        />
                    </span>
                );
            } else if (smtpAddress || displayName) {
                return (
                    <div className={styles.persona}>
                        <PersonaControl
                            name={displayName}
                            emailAddress={smtpAddress}
                            size={PersonaSize.size32}
                            showPersonaDetails={false}
                        />
                    </div>
                );
            }
            return null;
        }
    }

    private renderAudio() {
        let { type } = this.props.notification as SkypeNotificationViewState;
        return (
            <audio
                autoPlay={true}
                loop={type === CALL_NOTIFICATION_TYPE}
                src={this.getNotificationAudioSrc(type)}
            />
        );
    }

    private onNotificationRemoved = () => {
        this.clearTimer();
    };

    private clearTimer() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    private getNotificationAudioSrc(notificationType: string): string {
        let audioFileName: string = null;
        switch (notificationType) {
            case IM_NOTIFICATION_TYPE:
            case SUMMARY_NOTIFICATION_TYPE:
                audioFileName = IM_AUDIO_FILENAME;
                break;
            case CALL_NOTIFICATION_TYPE:
                audioFileName = CALL_AUDIO_FILENAME;
                break;
        }
        return audioFileName ? `${getPackageBaseUrl()}resources/sounds/${audioFileName}` : null;
    }

    private getNotificationDisplayTime(notificationType: string): number {
        switch (notificationType) {
            case IM_NOTIFICATION_TYPE:
            case SUMMARY_NOTIFICATION_TYPE:
                return IM_NOTIFICATION_TIMEOUT;
            case CALL_NOTIFICATION_TYPE:
                return CALL_NOTIFICATION_TIMEOUT;
            default:
                return null;
        }
    }
}
